import {Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {CatalogService} from "../../services/catalog/catalog.service";
import {HouseholdService} from "../../services/property/household.service";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {catchError, debounceTime, distinctUntilChanged} from "rxjs/operators";
import {Observable, of} from "rxjs";
import {CatalogProduct, CatalogSearchPage} from "../../models/catalog";
import {CommonModule} from "@angular/common";
import {ProxyImageDirective} from "../../directives/proxy-image.directive";
import {UiInputComponent} from "../../shared/ui";
import { AppLogger, AppLoggerService } from '../../core/logging';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {PRODUCT_PLACEHOLDER_ICON} from '../../shared/product-placeholder-icon';

@Component({
  selector: 'app-catalog-search',
  templateUrl: './catalog-search.component.html',
  styleUrl: './catalog-search.component.scss',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ProxyImageDirective, UiInputComponent, FontAwesomeModule]
})
export class CatalogSearchComponent implements OnInit, OnChanges, OnDestroy {

  private readonly log: AppLogger;

  /** Number of results fetched per page (infinite scroll). */
  private readonly pageSize = 20;

  @Input() searchTerm: string = '';
  @Input() householdId?: number;
  /** When set (e.g. a receipt's retailer name), only that provider is pre-selected instead of all enabled ones. */
  @Input() preselectProvider?: string;
  /**
   * Optional CSS max-height (e.g. '60vh', '24rem') for the results list. When set,
   * only the results scroll while the search box and provider filters stay fixed.
   */
  @Input() resultsMaxHeight?: string;
  @Output() itemSelected = new EventEmitter<CatalogProduct>();

  faProductPlaceholder = PRODUCT_PLACEHOLDER_ICON;

  selectedItem?: CatalogProduct;
  hoveredItemId: string | null = null;

  /** Viewport anchored preview positioning (fixed). */
  previewTopPx: number | null = null;
  previewLeftPx: number | null = null;
  previewPlacement: 'right' | 'left' = 'right';

  searchControl = new FormControl('');
  products: CatalogProduct[] = [];

  /** Pagination / infinite-scroll state. */
  loadingMore = false;
  private currentPage = 0;
  private total = 0;
  /** Monotonic id so stale (superseded) responses are discarded. */
  private searchSeq = 0;

  /** Sentinel at the end of the list; observed to trigger loading the next page. */
  @ViewChild('scrollAnchor') set scrollAnchor(el: ElementRef<HTMLElement> | undefined) {
    this.observeAnchor(el?.nativeElement);
  }
  private intersectionObserver?: IntersectionObserver;
  private observedAnchor?: HTMLElement;

  // Badge filter state — populated dynamically from household providers, falls back to hardcoded list
  providerBadges: { label: string; value: string }[] = [
    {label: 'Migros', value: 'migros'},
    {label: 'Open Foodfacts', value: 'off'}
  ];

  selectedProviders = new Set<string>(['migros', 'off']);

  constructor(private catalogService: CatalogService, private householdService: HouseholdService, private appLogger: AppLoggerService, private zone: NgZone) {
    this.log = this.appLogger.createForClass(CatalogSearchComponent);

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(() => this.reloadFromFirstPage());
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  /** True while more pages remain to be loaded for the current query. */
  get hasMore(): boolean {
    return this.products.length < this.total;
  }

  ngOnInit(): void {
    this.searchControl.setValue(this.searchTerm);
    if (this.householdId) {
      this.loadProviders(this.householdId);
    }
  }

  private loadProviders(householdId: number): void {
    this.householdService.getProviderSettings(householdId).subscribe({
      next: (settings) => {
        const enabled = settings.filter(s => s.is_enabled).sort((a, b) => a.priority - b.priority);
        if (enabled.length === 0) return;
        this.providerBadges = enabled.map(s => ({ label: s.provider_name || s.provider_key, value: s.provider_key }));

        const preselect = this.preselectProvider?.trim().toLowerCase();
        const match = preselect
          ? enabled.find(s => s.provider_key.toLowerCase() === preselect || (s.provider_name ?? '').toLowerCase() === preselect)
          : undefined;
        this.selectedProviders = match ? new Set([match.provider_key]) : new Set(enabled.map(s => s.provider_key));

        this.reloadFromFirstPage();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm']) {
      const next = (changes['searchTerm'].currentValue ?? '') as string;
      // Update the control without emitting to avoid duplicate searches;
      // the user can type further or we can trigger a search explicitly.
      this.searchControl.setValue(next, {emitEvent: true});
    }
  }

  private getSelectedProvidersArray(): string[] {
    return Array.from(this.selectedProviders);
  }

  /** Fetches a single page for the current query + provider selection. */
  private fetchPage(query: string, page: number): Observable<CatalogSearchPage | null> {
    if (!query || query.length < 3) return of(null);

    const providers = this.getSelectedProvidersArray();

    return this.catalogService.searchCatalogProduct(query, providers, this.pageSize, page).pipe(
      catchError(err => {
        this.log.error('Catalog search failed', err, { query, providers, page });
        return of(null);
      })
    );
  }

  /** Runs a fresh search from page 1, replacing the current results. */
  private reloadFromFirstPage(): void {
    const query = this.searchControl.value ?? '';
    const seq = ++this.searchSeq;

    this.currentPage = 0;
    this.total = 0;
    this.products = [];
    this.loadingMore = false;

    if (query.length < 3) return;

    this.fetchPage(query, 1).subscribe(res => {
      if (seq !== this.searchSeq || !res) return;
      this.products = res.items;
      this.total = res.total;
      this.currentPage = res.page;
    });
  }

  /** Appends the next page of results (triggered by scrolling to the bottom). */
  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;

    const query = this.searchControl.value ?? '';
    const seq = this.searchSeq;
    this.loadingMore = true;

    this.fetchPage(query, this.currentPage + 1).subscribe(res => {
      // A newer search may have started while this request was in flight.
      if (seq !== this.searchSeq) return;
      this.loadingMore = false;
      if (!res) return;
      this.products = [...this.products, ...res.items];
      this.total = res.total;
      this.currentPage = res.page;
    });
  }

  private observeAnchor(el?: HTMLElement): void {
    if (el === this.observedAnchor) return;
    this.intersectionObserver?.disconnect();
    this.observedAnchor = el;
    if (!el) return;

    if (!this.intersectionObserver) {
      this.intersectionObserver = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) {
          // IntersectionObserver callbacks are not guaranteed to run inside
          // Angular's zone, so re-enter it to keep change detection working.
          this.zone.run(() => this.loadMore());
        }
      }, { rootMargin: '200px' });
    }
    this.intersectionObserver.observe(el);
  }

  toggleProvider(provider: string): void {
    if (this.selectedProviders.has(provider)) {
      this.selectedProviders.delete(provider);
    } else {
      this.selectedProviders.add(provider);
    }

    // Re-run search from the first page when filters change.
    this.reloadFromFirstPage();
  }

  isProviderSelected(provider: string): boolean {
    return this.selectedProviders.has(provider);
  }

  selectItem(item: CatalogProduct) {
    this.selectedItem = item;
    this.itemSelected.emit(item); // For parent components
    this.log.debug('Catalog item selected', { name: item?.name, item });
  }

  onItemPreviewEnter(item: CatalogProduct, anchorEl?: HTMLElement): void {
    this.hoveredItemId = item?.id ?? null;

    // Compute fixed-position coordinates to avoid being clipped by scroll/overflow containers.
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const padding = 12;
      const desiredWidth = 420;

      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;

      this.previewPlacement = spaceRight >= (desiredWidth + padding) ? 'right' : 'left';

      // Vertically center relative to the row, and keep within viewport.
      const centerY = rect.top + rect.height / 2;
      const approxHeight = 140; // heuristic; CSS maxes content anyway
      const top = Math.min(
        Math.max(8, centerY - approxHeight / 2),
        Math.max(8, window.innerHeight - approxHeight - 8)
      );

      const left = this.previewPlacement === 'right'
        ? rect.right + padding
        : Math.max(8, rect.left - padding - Math.min(desiredWidth, window.innerWidth - 16));

      this.previewTopPx = top;
      this.previewLeftPx = left;
    }

    this.log.trace('Catalog hover preview enter', {
      id: item?.id,
      name: item?.name,
      hoveredItemId: this.hoveredItemId,
      previewTopPx: this.previewTopPx,
      previewLeftPx: this.previewLeftPx,
      previewPlacement: this.previewPlacement
    });
  }

  onItemPreviewLeave(item: CatalogProduct): void {
    if (this.hoveredItemId === (item?.id ?? null)) {
      this.hoveredItemId = null;
    }
    this.previewTopPx = null;
    this.previewLeftPx = null;
  }

  getProviderLogoPath(provider: string): Observable<string> {
    if (!provider) return new Observable(observer => {
      observer.next('assets/img/provider-logos/unknown.png');
      observer.complete();
    })

    return new Observable(observer => {
      observer.next('assets/img/provider-logos/' +
        provider.toLowerCase().replace(/ /g, '-') +
        '.png');
      observer.complete();
    })
  }

}
